import { useState } from "react";
import { Button, Form, Input, Select, message } from "antd";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../lib/axios";
import { useUserStore } from "../../../store/slices/user";

const TOTAL_STEPS = 7;

const INDUSTRY_OPTIONS = [
  { value: "hvac", label: "HVAC" },
  { value: "electrical", label: "Electrical" },
  { value: "plumbing", label: "Plumbing" },
  { value: "fire_safety", label: "Fire Safety" },
  { value: "pest_control", label: "Pest Control" },
  { value: "security", label: "Security" },
  { value: "cleaning", label: "Cleaning" },
  { value: "compliance", label: "Compliance" },
  { value: "other", label: "Other" },
];

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "dispatcher", label: "Dispatcher" },
  { value: "finance", label: "Finance" },
  { value: "field_worker", label: "Field Worker" },
  { value: "viewer", label: "Viewer" },
];

interface TeamMember { name: string; email: string; role: string; }

const StepDots = ({ current }: { current: number }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {Array.from({ length: TOTAL_STEPS }, (_, i) => (
      <div
        key={i}
        className={`rounded-full transition-all duration-200 ${
          i < current
            ? "w-2 h-2 bg-blue-600"
            : i === current
            ? "w-8 h-2 bg-blue-600"
            : "w-2 h-2 bg-slate-200"
        }`}
      />
    ))}
  </div>
);

const ProgressBar = ({ current }: { current: number }) => (
  <div className="w-full bg-slate-100 rounded-full h-1 mb-2">
    <div
      className="bg-blue-600 h-1 rounded-full transition-all duration-300"
      style={{ width: `${((current + 1) / TOTAL_STEPS) * 100}%` }}
    />
  </div>
);

export default function SetupWizard() {
  const navigate = useNavigate();
  const { user } = useUserStore();

  const [step, setStep] = useState(0);
  const [completing, setCompleting] = useState(false);

  const [companyForm] = Form.useForm();
  const [industryForm] = Form.useForm();
  const [clientForm] = Form.useForm();
  const [jobTypeForm] = Form.useForm();

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([{ name: "", email: "", role: "field_worker" }]);

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const [saving, setSaving] = useState(false);

  const handleNext = async () => {
    try {
      if (step === 0) {
        const values = await companyForm.validateFields();
        setSaving(true);
        try {
          // axiosInstance's baseURL already ends in "/company/v1" — this must NOT
          // repeat that prefix (that produced a doubled, 500ing URL). The real
          // company-profile endpoint is PUT /my-company (update-profile only
          // updates the logged-in user's own email/phone, not the company).
          await axiosInstance.put("/my-company", values);
        } catch (err: any) {
          message.error(err?.message || "Failed to save company profile. Please try again.");
          return;
        } finally {
          setSaving(false);
        }
      }
      if (step === 1) {
        await industryForm.validateFields();
      }
      if (step === 2) {
        const invitees = teamMembers.filter((m) => m.name.trim() && m.email.trim());
        if (invitees.length) {
          setSaving(true);
          try {
            const results = await Promise.allSettled(
              invitees.map((m) => {
                const [first_name, ...rest] = m.name.trim().split(/\s+/);
                return axiosInstance.post("/staff/create", {
                  first_name,
                  last_name: rest.join(" ") || undefined,
                  email: m.email.trim(),
                  role_slug: m.role,
                  // Temporary password — the invite email flow (fired server-side
                  // on staff/create) is what gets the user to actually set their own.
                  password: Math.random().toString(36).slice(2) + "A1!",
                });
              }),
            );
            const failed = results.filter((r) => r.status === "rejected").length;
            if (failed) {
              message.warning(`${failed} of ${invitees.length} invite(s) could not be sent.`);
            }
          } finally {
            setSaving(false);
          }
        }
      }
      if (step === 3) {
        // This step is explicitly optional (see stepDescriptions — "you can
        // skip this for now") and has its own Skip button, so a completely
        // untouched form advancing is correct. The gap was: if the user
        // *did* type something (e.g. address/email) but left the required
        // client_name blank, validateFields() rejected, the catch swallowed
        // it to null, and next() still ran — silently discarding their
        // input with zero feedback, indistinguishable from an intentional
        // skip. Block the advance and tell them, instead of losing it.
        const touchedAnyField = Object.values(clientForm.getFieldsValue()).some(
          (v) => typeof v === "string" && v.trim()
        );
        const valid = await clientForm.validateFields().catch(() => null);
        if (valid && valid.client_name?.trim()) {
          setSaving(true);
          try {
            await axiosInstance.post("/clients/create", {
              client_type: "commercial",
              business_name: valid.client_name,
              address_line1: valid.client_address || undefined,
              email: valid.contact_email || undefined,
              first_name: valid.contact_name || undefined,
            });
          } catch (err: any) {
            message.error(err?.message || "Failed to save client. You can add it later from Clients.");
          } finally {
            setSaving(false);
          }
        } else if (touchedAnyField) {
          message.error("Please enter a client name to continue, or use Skip to leave this step blank.");
          return;
        }
      }
      if (step === 4) {
        // Same gap as step 3 above, same fix: don't silently discard a
        // partially-filled, invalid form as if it were an intentional skip.
        const touchedAnyField = Object.values(jobTypeForm.getFieldsValue()).some(
          (v) => typeof v === "string" && v.trim()
        );
        const valid = await jobTypeForm.validateFields().catch(() => null);
        if (valid && valid.job_type_name?.trim()) {
          setSaving(true);
          try {
            await axiosInstance.post("/job-config/job-types/create", {
              name: valid.job_type_name,
              description: valid.job_type_description || undefined,
            });
          } catch (err: any) {
            message.error(err?.message || "Failed to save job type. You can add it later from Job Config.");
          } finally {
            setSaving(false);
          }
        } else if (touchedAnyField) {
          message.error("Please enter a job type name to continue, or use Skip to leave this step blank.");
          return;
        }
      }
      next();
    } catch {
      // validation error shown by antd
    }
  };

  const handleSkip = () => next();

  // The wizard is rendered by App.tsx's wildcard route whenever onboarding is
  // incomplete, independent of the current URL — so navigate()-ing to a settings
  // page from inside it just changes the address bar while the same wizard stays
  // on screen. Finish onboarding first (same as Done), then land on the real
  // Integrations tab (a query param, not a "/settings/integrations" path, which
  // has no route and would 404).
  const goToIntegrations = async () => {
    setCompleting(true);
    try {
      await axiosInstance.post("/admin/onboarding-complete", {});
      navigate("/settings?tab=integrations");
    } catch {
      message.error("Failed to complete setup. Please try again.");
    } finally {
      setCompleting(false);
    }
  };

  const handleDone = async () => {
    setCompleting(true);
    try {
      // POST, not PATCH — and no "/api" / "/company/v1" prefix duplicating the
      // axios baseURL. Only navigate away once this actually succeeds, otherwise
      // the user is stuck showing "setup incomplete" forever with no way out.
      await axiosInstance.post("/admin/onboarding-complete", {});
      navigate("/");
    } catch {
      message.error("Failed to complete setup. Please try again.");
    } finally {
      setCompleting(false);
    }
  };

  const addTeamRow = () =>
    setTeamMembers((prev) => [...prev, { name: "", email: "", role: "field_worker" }]);

  const removeTeamRow = (idx: number) =>
    setTeamMembers((prev) => prev.filter((_, i) => i !== idx));

  const updateTeamRow = (idx: number, field: keyof TeamMember, val: string) =>
    setTeamMembers((prev) =>
      prev.map((m, i) => (i === idx ? { ...m, [field]: val } : m))
    );

  const stepTitles = [
    "Company Profile",
    "Your Industry",
    "Invite Your Team",
    "Add First Client",
    "Create First Job Type",
    "Connect Accounting",
    "Download the App",
  ];

  const stepDescriptions = [
    "Tell us about your business",
    "Confirm the industry you operate in",
    "Get your team set up — you can skip this for now",
    "Add your first client — you can skip this for now",
    "Define your first job type — you can skip this for now",
    "Connect your accounting software — you can skip this for now",
    "Take ARRUK mobile with the engineer app",
  ];

  const isSkippable = [false, false, true, true, true, true, false];
  const isFinalStep = step === TOTAL_STEPS - 1;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-8 pt-8 pb-6">
          <ProgressBar current={step} />
          <p className="text-right text-xs text-slate-400 mb-6">
            Step {step + 1} of {TOTAL_STEPS}
          </p>

          <StepDots current={step} />

          <h2 className="text-2xl font-semibold text-slate-800 text-center mb-1">
            {stepTitles[step]}
          </h2>
          <p className="text-sm text-slate-400 text-center mb-8">
            {stepDescriptions[step]}
          </p>

          {/* ── Step 0: Company Profile ── */}
          {step === 0 && (
            <Form form={companyForm} layout="vertical" size="middle">
              <div className="grid grid-cols-2 gap-x-4">
                <Form.Item name="name" label="Company Name" rules={[{ required: true }]}>
                  <Input placeholder="Acme Field Services Ltd" />
                </Form.Item>
                <Form.Item name="vat_number" label="VAT Number">
                  <Input placeholder="GB123456789" />
                </Form.Item>
              </div>
              <Form.Item name="company_registration_number" label="Companies House Number">
                <Input placeholder="12345678" />
              </Form.Item>
              <Form.Item name="address_line1" label="Address Line 1">
                <Input placeholder="123 High Street" />
              </Form.Item>
              <div className="grid grid-cols-3 gap-x-4">
                <Form.Item name="city" label="City"><Input placeholder="London" /></Form.Item>
                <Form.Item name="county" label="County"><Input placeholder="Greater London" /></Form.Item>
                <Form.Item name="postcode" label="Postcode"><Input placeholder="SW1A 1AA" /></Form.Item>
              </div>
            </Form>
          )}

          {/* ── Step 1: Industry ── */}
          {step === 1 && (
            <Form form={industryForm} layout="vertical" size="middle">
              <Form.Item name="industry" label="Primary Industry" initialValue={(user as any)?.industry ?? undefined}>
                <Select options={INDUSTRY_OPTIONS} placeholder="Select your industry" size="large" />
              </Form.Item>
              <p className="text-xs text-slate-400">
                This controls default job types, compliance requirements, and terminology across the platform.
              </p>
            </Form>
          )}

          {/* ── Step 2: Invite Team ── */}
          {step === 2 && (
            <div className="space-y-3">
              {teamMembers.map((member, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 items-center">
                  <Input
                    placeholder="Full name"
                    value={member.name}
                    onChange={(e) => updateTeamRow(idx, "name", e.target.value)}
                  />
                  <Input
                    placeholder="Email address"
                    value={member.email}
                    onChange={(e) => updateTeamRow(idx, "email", e.target.value)}
                  />
                  <Select
                    options={ROLE_OPTIONS}
                    value={member.role}
                    onChange={(v) => updateTeamRow(idx, "role", v)}
                  />
                  {teamMembers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTeamRow(idx)}
                      className="text-red-400 hover:text-red-600 text-lg leading-none"
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addTeamRow}
                className="text-blue-600 text-sm hover:underline mt-1"
              >
                + Add another team member
              </button>
            </div>
          )}

          {/* ── Step 3: First Client ── */}
          {step === 3 && (
            <Form form={clientForm} layout="vertical" size="middle">
              <Form.Item name="client_name" label="Client Name" rules={[{ required: true }]}>
                <Input placeholder="Riverside Properties Ltd" />
              </Form.Item>
              <Form.Item name="client_address" label="Address">
                <Input placeholder="456 Park Lane, London" />
              </Form.Item>
              <div className="grid grid-cols-2 gap-x-4">
                <Form.Item name="contact_name" label="Contact Name">
                  <Input placeholder="Jane Smith" />
                </Form.Item>
                <Form.Item name="contact_email" label="Contact Email">
                  <Input placeholder="jane@riverside.co.uk" />
                </Form.Item>
              </div>
            </Form>
          )}

          {/* ── Step 4: First Job Type ── */}
          {step === 4 && (
            <Form form={jobTypeForm} layout="vertical" size="middle">
              <Form.Item name="job_type_name" label="Job Type Name" rules={[{ required: true }]}>
                <Input placeholder="e.g. Boiler Service" />
              </Form.Item>
              <Form.Item name="job_type_description" label="Description">
                <Input.TextArea rows={3} placeholder="Brief description of this job type" />
              </Form.Item>
            </Form>
          )}

          {/* ── Step 5: Connect Accounting ── */}
          {step === 5 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500 text-center">
                Connect your accounting software to sync invoices and payments automatically.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { name: "Xero", color: "bg-blue-500", desc: "Accounting & invoicing" },
                  { name: "QuickBooks", color: "bg-green-500", desc: "Bookkeeping & payroll" },
                  { name: "Sage", color: "bg-emerald-600", desc: "Business management" },
                  { name: "FreeAgent", color: "bg-orange-500", desc: "Small business accounting" },
                ].map((app) => (
                  <button
                    key={app.name}
                    type="button"
                    onClick={goToIntegrations}
                    className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/40 transition-all text-left"
                  >
                    <div className={`w-9 h-9 ${app.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <span className="text-white text-xs font-bold">{app.name[0]}</span>
                    </div>
                    <div>
                      <div className="font-medium text-slate-800 text-sm">{app.name}</div>
                      <div className="text-xs text-slate-400">{app.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 text-center">
                You can also connect these later from{" "}
                <span className="text-blue-600 cursor-pointer" onClick={() => navigate("/settings/integrations")}>
                  Settings → Integrations
                </span>
              </p>
            </div>
          )}

          {/* ── Step 6: Download App ── */}
          {step === 6 && (
            <div className="text-center space-y-5">
              <p className="text-sm text-slate-500">
                ARRUK engineers use the mobile app to receive jobs, capture signatures, photos and time logs — even offline.
              </p>
              <div className="flex justify-center">
                <div className="w-36 h-36 bg-slate-100 rounded-2xl flex items-center justify-center border border-slate-200">
                  <div className="text-center">
                    <div className="text-slate-400 text-xs mb-1">QR Code</div>
                    <div className="w-20 h-20 bg-slate-200 rounded-lg mx-auto grid grid-cols-5 gap-0.5 p-1">
                      {Array.from({ length: 25 }).map((_, i) => (
                        <div
                          key={i}
                          className={`rounded-sm ${Math.random() > 0.5 ? "bg-slate-700" : "bg-white"}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-center gap-3">
                <a
                  href="https://apps.apple.com"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors no-underline"
                >
                  <span>App Store</span>
                </a>
                <a
                  href="https://play.google.com"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors no-underline"
                >
                  <span>Play Store</span>
                </a>
              </div>
              <p className="text-xs text-slate-400">
                You can always download the app later from the app stores.
              </p>
            </div>
          )}

          {/* ── Navigation ── */}
          <div className="flex items-center justify-between mt-8 pt-5 border-t border-slate-100">
            <div className="flex items-center gap-3">
              {step > 0 && (
                <button
                  type="button"
                  onClick={back}
                  className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
                >
                  &larr; Back
                </button>
              )}
              {isSkippable[step] && !isFinalStep && (
                <button
                  type="button"
                  onClick={handleSkip}
                  className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
                >
                  Skip
                </button>
              )}
            </div>

            {isFinalStep ? (
              <Button
                type="primary"
                size="large"
                className="px-8 rounded-xl"
                loading={completing}
                onClick={handleDone}
              >
                Done!
              </Button>
            ) : (
              <Button
                type="primary"
                size="large"
                className="px-8 rounded-xl"
                loading={saving}
                onClick={handleNext}
              >
                Next &rarr;
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
